import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusEditor } from './StatusEditor';
import { DeleteStatusReassignDialog } from './DeleteStatusReassignDialog';
import {
  useProjectStatuses,
  useCreateProjectStatus,
  useUpdateProjectStatus,
  useDeleteProjectStatus,
  useReorderProjectStatuses,
} from '@/hooks/use-projects';
import { toast } from '@/components/ui/toaster';
import { isStatusDeleteTasksConflict } from '@/lib/api';
import { useState } from 'react';

type ApiErrorLike = { response?: { data?: { message?: string } } };

interface ProjectStatusesDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectStatusesDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: ProjectStatusesDialogProps) {
  const { data: statuses = [], isLoading } = useProjectStatuses(projectId);
  const createStatus = useCreateProjectStatus();
  const updateStatus = useUpdateProjectStatus();
  const deleteStatus = useDeleteProjectStatus();
  const reorderStatuses = useReorderProjectStatuses();
  const [reassign, setReassign] = useState<{ statusId: string; taskCount: number } | null>(null);

  const handleCreate = async (data: { name: string; color: string; isDoneState: boolean }) => {
    try {
      await createStatus.mutateAsync({
        projectId,
        ...data,
      });
      toast({ title: 'Status created' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to create status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleUpdate = async (
    statusId: string,
    data: { name?: string; color?: string; isDoneState?: boolean }
  ) => {
    try {
      await updateStatus.mutateAsync({
        projectId,
        statusId,
        ...data,
      });
      toast({ title: 'Status updated' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to update status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDelete = async (statusId: string) => {
    try {
      await deleteStatus.mutateAsync({ projectId, statusId });
      toast({ title: 'Status deleted' });
    } catch (error: unknown) {
      if (isStatusDeleteTasksConflict(error)) {
        const taskCount = error.response?.data?.taskCount ?? 0;
        setReassign({ statusId, taskCount });
        return;
      }
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to delete status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleReassignConfirm = async (replacementStatusId: string) => {
    if (!reassign) return;
    try {
      await deleteStatus.mutateAsync({
        projectId,
        statusId: reassign.statusId,
        replacementStatusId,
      });
      toast({
        title: 'Status deleted',
        description: 'Existing tasks were moved to the selected status.',
      });
      setReassign(null);
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to delete status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleReorder = async (statusIds: string[]) => {
    try {
      await reorderStatuses.mutateAsync({ projectId, statusIds });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to reorder statuses',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Project Statuses</DialogTitle>
            <DialogDescription>
              Manage default statuses for <strong>{projectName}</strong>.
              These statuses are used by all lists that don't have custom statuses.
            </DialogDescription>
          </DialogHeader>

          <StatusEditor
            statuses={statuses}
            isLoading={isLoading}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onReorder={handleReorder}
            isCreating={createStatus.isPending}
            isUpdating={updateStatus.isPending}
          />
        </DialogContent>
      </Dialog>

      <DeleteStatusReassignDialog
        open={!!reassign}
        onOpenChange={(next) => !next && setReassign(null)}
        excludeStatusId={reassign?.statusId ?? ''}
        taskCount={reassign?.taskCount ?? 0}
        statuses={statuses}
        isPending={deleteStatus.isPending}
        onConfirm={handleReassignConfirm}
      />
    </>
  );
}
